import type { ComponentProps, ElementType } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  AlignItemsProps,
  BackgroundProps,
  BorderProps,
  DisplayProps,
  JustifyContentProps,
  MarginLeftProps,
  MaxWidthProps,
  SizeProps,
} from 'styled-system';
import {
  alignItems,
  background,
  border,
  display,
  fontWeight,
  justifyContent,
  marginLeft,
  maxWidth,
  size,
  space,
  themeGet,
} from 'styled-system';

import Icon from 'common/components/Icon';
import { Link } from 'common/components/Link';
import { HeadlineH8 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { ModifiersProps } from 'common/theme/helper';
import { modifiers as systemModifiers } from 'common/theme/helper';
import type { SpaceProps } from 'common/theme/system-types';
import type { AsProps, BoxSizingProps, WhiteSpaceProps } from 'common/theme/system-utilities';
import { boxSizing, rotate, whiteSpace } from 'common/theme/system-utilities';
import type { PrimaryNavigationItemProps } from 'common/types/NavigationItem';
import { Element, NavigationParent } from 'common/types/tracking';
import { openSideNav, SideNavType } from 'common/utils/navigation';
import { useWrapItems } from './hooks/wrap-items';
import { trackNavigationItemClick } from './utils/tracking';

const Container = styled.div<
  DisplayProps & BoxSizingProps & JustifyContentProps & MaxWidthProps & SpaceProps & WhiteSpaceProps
>`
  ${boxSizing};
  ${display};
  ${justifyContent};
  ${space};
  ${maxWidth};
  ${whiteSpace};
`;

const containerStyles: ComponentProps<typeof Container> = {
  display: ['none', null, 'flex'],
  boxSizing: 'border-box',
  maxWidth: 1600,
  mx: 'auto',
  px: [null, null, 'sp_48', 'sp_64'],
};

const NavigationButton = styled(Link)<
  AsProps &
    BackgroundProps &
    BorderProps &
    DisplayProps &
    AlignItemsProps &
    SpaceProps &
    ModifiersProps
>`
  ${background};
  ${border};
  ${display};
  ${alignItems};
  ${fontWeight};
  ${space};

  &:last-child {
    margin-right: 0;
  }
  text-decoration: none !important;

  ${props => ({
    '&:hover': {
      paddingBottom: themeGet('space.sp_12')(props) - 3,
      borderBottomStyle: 'solid',
      borderBottomWidth: themeGet('radii.small')(props),
      borderBottomColor: themeGet('colors.sys.primary.border.default')(props),
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  })};
`;

const buttonStyles: ComponentProps<typeof NavigationButton> = {
  background: 'none',
  border: 'none',
  display: 'flex',
  alignItems: 'baseline',
  m: 'sp_0',
  p: 'sp_0',
  py: 'sp_12',
};

export const buttonAriaAttributes = {
  'aria-haspopup': 'dialog',
  'aria-controls': 'side-navigation',
} as const;

const NavigationTypography = styled(HeadlineH8)<ModifiersProps>`
  ${systemModifiers};
`;

const isLink = (href?: Nullable<string>) => typeof href === 'string' && href.length > 0;

const RightArrow = styled(Icon)<SizeProps & MarginLeftProps>`
  ${size};
  ${rotate};
  ${marginLeft};
`;

const rightArrowStyles: ComponentProps<typeof RightArrow> = {
  name: 'arrowDownStrong',
  size: 12,
  rotate: '-90',
  ml: 'sp_8',
};

interface PrimaryNavigationProps {
  items: PrimaryNavigationItemProps[];
  withArrows: boolean;
  wrapItems: boolean;
  sideNavType: SideNavType;
  shopName?: string;
}

const getNavigationParentComponent = (sideNavType: SideNavType) =>
  sideNavType === SideNavType.Global
    ? NavigationParent.GlobalHeader
    : NavigationParent.ShopInShopNav;

export const PrimaryNavigation = ({
  items,
  withArrows,
  wrapItems,
  sideNavType,
  shopName,
}: PrimaryNavigationProps) => {
  const { t } = useLocalization();
  const { visibleItemsCount, containerRef, itemsRef } = useWrapItems(items, wrapItems);

  return (
    <Container
      ref={containerRef}
      justifyContent={wrapItems ? 'flex-start' : 'space-between'}
      {...containerStyles}
      {...(wrapItems ? { whiteSpace: 'nowrap' } : {})}
    >
      {items.slice(0, visibleItemsCount).map(({ name, modifiers, href }, index) => {
        const trackClick = () =>
          trackNavigationItemClick({
            parentComponent: getNavigationParentComponent(sideNavType),
            element: isLink(href) ? Element.Link : Element.Button,
            label: name,
            length: items.length,
            position: index,
            shopName,
          });
        const navigationProps = isLink(href)
          ? { href, onClick: trackClick }
          : {
              as: 'button' as ElementType,
              onClick: () => {
                openSideNav({ selectedItem: name, type: sideNavType });
                trackClick();
              },
              ...buttonAriaAttributes,
            };

        return (
          <NavigationButton
            key={name}
            ref={ref => {
              if (itemsRef.current) {
                itemsRef.current[index] = ref;
              }
            }}
            mr={wrapItems ? 'sp_24' : 'sp_4'}
            {...buttonStyles}
            {...navigationProps}
          >
            <NavigationTypography as="span" color="sys.neutral.text.default" modifiers={modifiers}>
              {name}
              {withArrows && href === null && <RightArrow {...rightArrowStyles} />}
            </NavigationTypography>
          </NavigationButton>
        );
      })}
      {wrapItems && visibleItemsCount < items.length && (
        <NavigationButton
          as="button"
          mr="sp_16"
          {...buttonStyles}
          {...buttonAriaAttributes}
          onClick={() => {
            openSideNav({ type: sideNavType });
            trackNavigationItemClick({
              parentComponent: getNavigationParentComponent(sideNavType),
              element: Element.Button,
              label: t('h24_top_navigation_other'),
              length: items.length,
              position: visibleItemsCount,
              shopName,
            });
          }}
        >
          <HeadlineH8 as="span" color="sys.neutral.text.default">
            {t('h24_top_navigation_other')}
            <RightArrow {...rightArrowStyles} />
          </HeadlineH8>
        </NavigationButton>
      )}
    </Container>
  );
};
